/* eslint-disable no-param-reassign */
/**
 * Mini template system to populate HTML.
 * Finds elements labeled with a specific data attribute, and sets their textContent, innerHTML, and/or
 * attributes.
 *
 * HTML syntax: `<div attribute="target1,target2=fieldA;target3=fieldB"></div>`
 * e.g. `<img data-x="src=imgSrcField;alt,data-name=imgNameField;"></img>`
 *
 * @param {Element} parentNode - Parent node under which `commandAttribute` is searched for
 * @param {string} commandAttribute - The HTML attribute used to identify elements that should be filled
 * @param {Record<string, any>} data - Object containing data to be filled
 * @param {{ keepIfEmpty?: boolean }} options -
 * - `keepIfEmpty` - If false (default), deletes node if value is null or empty
 */
export const fillTemplate = (parentNode, commandAttribute, data, { keepIfEmpty = false } = {}) => {

    /**
     * Helper: removes element if null or empty if options to keep are not set
     * @param {Element} el
     * @param {any} value
     * @returns {boolean} True if element was removed, else false.
     */
    const removeIfEmpty = (el, value) => {
        if (!keepIfEmpty && (value == null || value === '' || value.length === 0)) {
            el.remove();
            return true;
        }
        return false;
    };

    // Get all nodes with the specified template attribute
    parentNode.querySelectorAll(`[${commandAttribute}]`).forEach((el) => {
        // The attribute value contains the commands specifying how the element should be populated
        const commands = el.getAttribute(commandAttribute);
        // Multiple commands are split by ";"
        commands?.split(';').forEach((command) => {
            // targets: specifies the action(s) to be taken
            // fields: specifies the field(s) that the content should be drawn from
            const [targets, fields] = command.split('=');
            // Multiple targets or values are split by ","
            targets.split(',').forEach((target) => {
                const values = fields.split(',').map((field) => data[field]);
                const value = values[0]; // Most commands only support one value
                // Possible actions
                switch (target) {
                case 'text':
                    if (!removeIfEmpty(el, value) && value != null) {
                        el.textContent = value;
                    }
                    break;
                case 'html':
                    if (!removeIfEmpty(el, value) && value != null) {
                        el.innerHTML = value;
                    }
                    break;
                case 'replace':
                    if (!removeIfEmpty(el, values)) {
                        // Save original text contents for repeat replacements
                        const replaceAttribute = `${commandAttribute}-replace`;
                        const replaceAttributeValue = el.getAttribute(replaceAttribute);
                        const formatString = replaceAttributeValue ?? el.textContent ?? '';
                        if (replaceAttributeValue == null) {
                            el.setAttribute(replaceAttribute, formatString);
                        }
                        el.textContent = formatString.replace(
                            /{(\d+)}/g,
                            (match, group) => values[Number(group)] ?? match
                        );
                    }
                    break;
                case 'test':
                    if (!value) el.remove();
                    break;
                case 'test!':
                    if (value) el.remove();
                    break;
                case 'alt':
                    // Render undefined/empty alt attributes
                    el.setAttribute(target, value != null ? value : '');
                    break;
                default:
                    if (!removeIfEmpty(el, value) && value != null) {
                        el.setAttribute(target, value);
                    }
                }
            });
        });
    });
};

/**
 * Fill the contents of an HTML template element using `fillTemplate`, and return the result.
 *
 * @param {HTMLTemplateElement} templateElement
 * @param {string} commandAttribute
 * @param {Record<string, any>} data
 * @param {{ keepIfEmpty?: boolean }} options
 * @returns {Node}
 */
export const createFilledElement = (templateElement, commandAttribute, data, options = {}) => {
    const template = templateElement.content.cloneNode(true);
    fillTemplate(template, commandAttribute, data, options);
    return template;
};
